import {
  AnnotatedJsonSchema,
  EntityDetailSchema,
  EntityDetailAttributesSchema,
} from '@web-config-app/core';

export const mockConfigEntitySchema: EntityDetailSchema = {
  type: 'object',
  properties: {
    id: {
      type: 'string',
    },
    type: {
      type: 'string',
      enum: ['config-entity'],
    },
    attributes: {
      type: 'object',
      properties: {
        entityMetadata: {
          type: 'object',
          properties: {
            name: {
              type: 'string',
            },
            status: {
              type: 'string',
              enum: ['draft', 'published', 'archived'],
            },
            lastUpdatedDate: {
              type: 'string',
              format: 'date-time',
            },
            lastPublishedDate: {
              type: 'string',
              format: 'date-time',
            },
            readOnly: {
              type: 'boolean',
            },
            domainId: {
              type: 'string',
            },
            domainEntityType: {
              type: 'string',
            },
            editorState: {
              type: 'string',
            },
            attachments: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  assetId: {
                    type: 'string',
                  },
                },
              },
            },
          },
        },
        domainEntityAttributes: {
          type: 'object',
        },
      },
    },
  },
  required: ['type', 'id'],
};

export const mockSimpleSchemaAttributes = {
  title: 'Simple Schema Attributes',
  type: 'object',
  'x-entity-label': {
    key: 'SIMPLE_SCHEMA_ENTITY_NAME',
  },
  'x-entity-inject-reference': {
    relationship: 'pygmyOwl',
    'x-entity-label': { key: 'PYGMY_OWL_LABEL' },
    'x-entity-presentation': {
      hint: {
        key: 'PYGMY_OWL_HINT',
      },
    },
  },
  properties: {
    entityMetadata: {
      'x-entity-presentation': {
        hidden: true,
      },
      type: 'object',
      properties: {
        status: {
          type: 'string',
          enum: ['draft', 'published', 'archived'],
        },
        lastUpdatedDate: {
          type: 'string',
          format: 'date-time',
        },
        lastPublishedDate: {
          type: 'string',
          format: 'date-time',
        },
      },
    },
    tags: {
      title: 'Example primitive array on root node',
      type: 'array',
      items: {
        type: 'string',
        title: 'Tag',
      },
    },
    metaData: {
      'x-entity-label': {
        key: 'METADATA',
      },
      type: 'object',
      properties: {
        internalName: {
          title: 'Name',
          'x-entity-label': {
            key: 'NAME',
          },
          type: 'string',
        },
      },
      required: ['internalName'],
    },
    primitiveText: {
      type: 'string',
      title: 'Text',
      'x-entity-label': {
        key: 'SIMPLE_SCHEMA_PRIMITIVE_TEXT_LABEL',
      },
    },
    complex: {
      type: 'object',
      title: 'Complex Object',
      'x-entity-label': {
        key: 'SIMPLE_SCHEMA_COMPLEX_OBJECT_LABEL',
      },
      properties: {
        primitiveEnum: {
          type: 'string',
          title: 'Enum',
          'x-entity-label': {
            key: 'SIMPLE_SCHEMA_PRIMITIVE_ENUM_LABEL',
          },
          enum: ['hi', 'bye'],
        },
        primitiveObject: {
          type: 'object',
          title: 'Nested primitive object',
          'x-entity-label': {
            key: 'SIMPLE_SCHEMA_COMPLEX_NESTED_OBJECT_LABEL',
          },
          properties: {
            name: {
              type: 'string',
              title: 'Name',
              'x-entity-label': {
                key: 'SIMPLE_SCHEMA_COMPLEX_NESTED_OBJECT_NAME_LABEL',
              },
            },
            age: {
              type: 'integer',
              title: 'Age',
              'x-entity-label': {
                key: 'SIMPLE_SCHEMA_COMPLEX_NESTED_OBJECT_AGE_LABEL',
              },
            },
          },
        },
      },
    },
    conditional: {
      'x-entity-label': {
        key: 'SIMPLE_SCHEMA_PRIMITIVE_CONDITIONAL_TREE_ITEM',
      },
      type: 'object',
      properties: {
        startDate: {
          'x-entity-label': {
            key: 'START_DATE',
          },
          type: 'string',
          format: 'date',
        },
        endDate: {
          'x-entity-label': {
            key: 'END_DATE',
          },
          type: 'string',
          format: 'date',
        },
      },
      'x-entity-conditional': {
        conditions: [
          {
            type: 'includeIf',
            rules: {
              '==': [{ var: 'entityMetadata.status' }, 'published'],
            },
          },
        ],
      },
    },
  },
  required: ['primitiveText', 'complex', 'status', 'metaData'],
} as EntityDetailAttributesSchema;

export const mockRelationshipDataItemSubSchema = {
  type: 'object',
  properties: {
    id: {
      type: 'string',
    },
    type: {
      type: 'string',
      enum: ['config-pygmy-owl'],
    },
  },
  required: ['id', 'type'],
};

export const mockRelationshipDataItemWithConstSubSchema = {
  type: 'object',
  properties: {
    id: {
      type: 'string',
    },
    type: {
      type: 'string',
      const: 'config-pygmy-owl',
    },
  },
  required: ['id', 'type'],
};

export const mockOneToOneRelationshipsSubSchema = {
  type: 'object',
  properties: {
    pygmyOwl: {
      type: 'object',
      properties: {
        data: mockRelationshipDataItemSubSchema,
      },
    },
  },
};

export const mockOneToOneRelationshipsSubSchemaWithConst = {
  type: 'object',
  properties: {
    pygmyOwl: {
      type: 'object',
      properties: {
        data: mockRelationshipDataItemWithConstSubSchema,
      },
    },
  },
};

export const mockOneToOneRelationshipTransformedSchema = {
  type: 'object',
  properties: {
    id: {
      type: 'string',
    },
    type: {
      type: 'string',
      enum: ['config-pygmy-owl'],
    },
  },
  required: ['id', 'type'],
  'x-entity-label': {
    key: 'PYGMY_OWL_LABEL',
  },
  'x-entity-presentation': {
    hint: {
      key: 'PYGMY_OWL_HINT',
    },
  },
  'x-entity-reference': {
    relationship: 'pygmyOwl',
    relationshipDataIdKey: 'id',
    includesDisplayNameKey: 'internalName',
  },
};

export const mockOneToManyRelationshipTransformedSchema = {
  ...mockOneToOneRelationshipTransformedSchema,
  'x-entity-reference': {
    relationship: 'campaigns',
  },
};

export const mockOneToManyRelationshipsSubSchema = {
  type: 'object',
  properties: {
    pygmyOwls: {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: mockRelationshipDataItemSubSchema,
        },
      },
    },
  },
};

export const mockOneToManyRelationshipsSubSchemaWithConst = {
  type: 'object',
  properties: {
    pygmyOwls: {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: mockRelationshipDataItemWithConstSubSchema,
        },
      },
    },
  },
};

export const mockSimpleSchemaAttributesWithCustomRelationshipProperties = {
  ...mockSimpleSchemaAttributes,
  'x-entity-inject-reference': {
    relationship:
      mockSimpleSchemaAttributes['x-entity-inject-reference']?.relationship,
    relationshipDataIdKey: 'custom-data-key',
    includesDisplayNameKey: 'custom-name-key',
    'x-entity-label':
      mockSimpleSchemaAttributes['x-entity-inject-reference']?.[
        'x-entity-label'
      ],
    'x-entity-presentation': {
      ...mockSimpleSchemaAttributes['x-entity-inject-reference']?.[
        'x-entity-presentation'
      ],
      banner: {
        title: {
          key: 'MOCK_ENTITY_REFERENCE_BANNER_TITLE',
        },
        status: 'info',
      },
    },
  },
} as const;

export const mockSimpleSchema = {
  type: 'object',
  'x-entity-metadata': {
    entityNamePath: '$.attributes.metaData.internalName',
  },
  properties: {
    id: {
      'x-entity-presentation': {
        hidden: true,
      },
      type: 'string',
    },
    type: {
      'x-entity-presentation': {
        hidden: true,
      },
      type: 'string',
      enum: ['config-cactus'],
    },
    attributes: mockSimpleSchemaAttributes,
    relationships: mockOneToOneRelationshipsSubSchema,
  },
  required: ['type', 'id'],
} as EntityDetailSchema;

export const mockConfigEntitySchemaWithDomainEntityAttributes = {
  ...mockConfigEntitySchema,
  properties: {
    ...mockConfigEntitySchema.properties,
    attributes: {
      ...mockConfigEntitySchema.properties.attributes,
      properties: {
        ...mockConfigEntitySchema.properties.attributes.properties,
        domainEntityAttributes: mockSimpleSchemaAttributes,
      },
    },
  },
};
export const mockConfigEntitySchemaWithDomainEntityAttributesAndRelationships =
  {
    ...mockConfigEntitySchema,
    properties: {
      ...mockConfigEntitySchema.properties,
      attributes:
        mockConfigEntitySchemaWithDomainEntityAttributes.properties.attributes,
      relationships: mockSimpleSchema.properties.relationships,
    },
  };
export const mockSimpleSchemaWithAssetReferenceProperties = {
  ...mockSimpleSchema,
  properties: {
    ...mockSimpleSchema.properties,
    attributes: {
      type: 'object',
      properties: {
        entityMetadata: mockSimpleSchemaAttributes.properties.entityMetadata,
        metaData: {
          type: 'object',
          properties: {
            iconId: {
              'x-entity-label': {
                key: 'ICON_ID',
              },
              'x-entity-asset': {
                type: 'image',
              },
              type: 'string',
            },
            badges: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  badgeId: {
                    'x-entity-label': {
                      key: 'BADGE_ICON_ID',
                    },
                    'x-entity-asset': {
                      type: 'image',
                    },
                    type: 'string',
                  },
                },
              },
            },
          },
        },
      },
    },
  },
} as EntityDetailSchema;

export const mockSimpleSchemaWithIncludeIfConditionalField = {
  type: 'object',
  'x-entity-metadata': {
    entityNamePath: '$.attributes.metaData.internalName',
  },
  properties: {
    id: {
      'x-entity-presentation': {
        hidden: true,
      },
      type: 'string',
    },
    type: {
      'x-entity-presentation': {
        hidden: true,
      },
      type: 'string',
      enum: ['config-cactus'],
    },
    attributes: {
      type: 'object',
      properties: {
        entityMetadata: mockSimpleSchemaAttributes.properties?.metaData,
        metaData: mockSimpleSchemaAttributes.properties?.metaData,
        conditionalData: {
          type: 'object',
          'x-entity-conditional': {
            conditions: [
              {
                type: 'includeIf',
                rules: {
                  '==': [{ var: 'entityMetadata.status' }, 'draft'],
                },
              },
            ],
          },
          properties: {
            conditionalFieldText: {
              type: 'string',
            },
          },
        },
      },
    },
  },
  required: ['type', 'id'],
} as AnnotatedJsonSchema;

export const mockSimpleSchemaWithOneToManyRelationship = {
  type: 'object',
  properties: {
    ...mockSimpleSchema.properties,
    relationships: mockOneToManyRelationshipsSubSchema,
  },
} as EntityDetailSchema;

export const primitiveArraySubSchema = {
  title: 'Things',
  type: 'array',
  'x-entity-label': {
    key: 'SIMPLE_SCHEMA_ARRAY_LABEL',
    propertyRef: 'examplePropertyForItemName',
    arrayItemNameKey: 'SIMPLE_SCHEMA_ARRAY_ITEM_LABEL',
  },
  items: {
    title: 'Stuff Array Item',
    type: 'object',
    properties: {
      examplePropertyForItemName: {
        type: 'string',
        title: 'Name',
      },
    },
  },
};

export const mockSimpleSchemaWithPrimitiveArrayAttributes = {
  ...mockSimpleSchemaAttributes,
  properties: {
    ...mockSimpleSchemaAttributes.properties,
    complexWithArray: {
      title: 'Object with array property',
      type: 'object',
      'x-entity-label': {},
      properties: {
        thing: {
          'x-entity-label': {
            key: 'SIMPLE_SCHEMA_THING_LABEL',
          },
          type: 'object',
          properties: {
            startDate: {
              'x-entity-label': {
                key: 'SIMPLE_SCHEMA_START_DATE',
              },
              type: 'string',
              format: 'date',
            },
            endDate: {
              'x-entity-label': {
                key: 'SIMPLE_SCHEMA_END_DATE',
              },
              type: 'string',
              format: 'date',
            },
          },
        },
        stuff: primitiveArraySubSchema,
      },
    },
  },
};

export const mockSimpleSchemaWithPrimitiveArray = {
  ...mockSimpleSchema,
  properties: {
    ...mockSimpleSchema.properties,
    attributes: mockSimpleSchemaWithPrimitiveArrayAttributes,
  },
};

export const complexArraySubSchema = {
  title: 'Things',
  type: 'array',
  'x-entity-label': {
    key: 'SIMPLE_SCHEMA_ARRAY_LABEL',
  },
  items: {
    'x-entity-label': {
      propertyRef: 'examplePropertyForItemName',
      key: 'SIMPLE_SCHEMA_ARRAY_ITEM_LABEL',
    },
    title: 'Stuff Array Item',
    type: 'object',
    properties: {
      examplePropertyForItemName: {
        type: 'string',
      },
      exampleObjectProperty: {
        type: 'object',
        properties: {
          one: {
            type: 'string',
          },
          two: {
            type: 'string',
          },
        },
      },
    },
  },
};

export const mockSimpleSchemaWithComplexArrayAttributes = {
  ...mockSimpleSchemaAttributes,
  properties: {
    ...mockSimpleSchemaAttributes.properties,
    complexWithArray: {
      title: 'Object with array property',
      type: 'object',
      'x-entity-label': {},
      properties: {
        thing: {
          'x-entity-label': {
            key: 'SIMPLE_SCHEMA_THING_LABEL',
          },
          type: 'object',
          properties: {
            startDate: {
              'x-entity-label': {
                key: 'SIMPLE_SCHEMA_START_DATE',
              },
              type: 'string',
              format: 'date',
            },
            endDate: {
              'x-entity-label': {
                key: 'SIMPLE_SCHEMA_END_DATE',
              },
              type: 'string',
              format: 'date',
            },
          },
        },
        stuff: complexArraySubSchema,
      },
    },
  },
};

export const mockSimpleSchemaWithComplexArray = {
  ...mockSimpleSchema,
  properties: {
    ...mockSimpleSchema.properties,
    attributes: mockSimpleSchemaWithComplexArrayAttributes,
  },
};

export const mockSchemaWithOneOfArrayItemPage = {
  ...mockSimpleSchema,
  properties: {
    ...mockSimpleSchema.properties,
    attributes: {
      type: 'object',
      title: 'Schema with Any Of Array',
      'x-entity-label': {
        key: 'THING',
      },
      properties: {
        entityMetadata:
          mockSimpleSchema.properties.attributes.properties?.entityMetadata,
        metaData: mockSimpleSchema.properties.attributes.properties?.metaData,
        contentData: {
          'x-entity-label': {
            key: 'CONTENT_DATA',
          },
          type: 'object',
          title: 'Content Data Tree Node',
          properties: {
            things: {
              type: 'array',
              'x-entity-label': {
                key: 'THINGS',
              },
              items: {
                discriminator: {
                  propertyName: 'thingType',
                },
                oneOf: [
                  {
                    'x-entity-label': {
                      key: 'THING_TYPE_ONE',
                    },
                    type: 'object',
                    title: 'Thing type one',
                    properties: {
                      thingType: {
                        'x-entity-label': {
                          key: 'THING_TYPE_ONE',
                        },
                        type: 'string',
                        enum: ['thingTypeOne'],
                      },
                      thingContent: {
                        type: 'object',
                        'x-entity-label': {
                          key: 'THING_CONTENT',
                        },
                        properties: {
                          thingName: {
                            'x-entity-label': {
                              key: 'THING_NAME',
                            },
                            type: 'string',
                          },
                          thingSize: {
                            'x-entity-label': {
                              key: 'THING_SIZE',
                            },
                            type: 'integer',
                          },
                        },
                      },
                    },
                    required: ['thingType'],
                  },
                ],
              },
            },
          },
        },
      },
    },
  },
};

export const mockOneOfObjectSchema = {
  type: 'object',
  'x-entity-label': {
    key: 'THINGS',
  },
  discriminator: {
    propertyName: 'thingType',
  },
  oneOf: [
    {
      'x-entity-label': {
        key: 'THING_TYPE_ONE',
      },
      type: 'object',
      title: 'Thing type one',
      properties: {
        thingType: {
          'x-entity-label': {
            key: 'THING_TYPE_ONE',
          },
          type: 'string',
          enum: ['thingTypeOne'],
        },
        thingContent: {
          type: 'object',
          'x-entity-label': {
            key: 'THING_CONTENT',
          },
          properties: {
            thingName: {
              'x-entity-label': {
                key: 'THING_NAME',
              },
              type: 'string',
            },
            thingSize: {
              'x-entity-label': {
                key: 'THING_SIZE',
              },
              type: 'integer',
            },
          },
        },
      },
    },
  ],
};

export const mockSchemaWithOneOfObject = {
  ...mockSimpleSchema,
  properties: {
    ...mockSimpleSchema.properties,
    attributes: {
      type: 'object',
      title: 'Schema with Any Of Array',
      'x-entity-label': {
        key: 'THING',
      },
      properties: {
        entityMetadata:
          mockSimpleSchema.properties.attributes.properties?.entityMetadata,
        metaData: mockSimpleSchema.properties.attributes.properties?.metaData,
        contentData: {
          'x-entity-label': {
            key: 'CONTENT_DATA',
          },
          type: 'object',
          title: 'Content Data Tree Node',
          properties: {
            things: mockOneOfObjectSchema,
          },
        },
      },
    },
  },
};

export const mockActivitiesCreateSchema = {
  title: 'Config Activity',
  type: 'object',
  description:
    'Model returned after a toolbox activity is created from config.',
  'x-entity-metadata': {
    entityNamePath: '$.attributes.metaData.internalName',
  },
  'x-entity-data-source-registry': {
    sources: [
      {
        name: 'dml',
        apiPath: '/v1/health-lfrs-flow-contract',
      },
    ],
  },
  properties: {
    id: {
      type: 'string',
      allowEmpty: false,
      'x-entity-localized': true,
    },
    type: {
      type: 'string',
      enum: ['config-activity'],
    },
    attributes: {
      title: 'ConfigActivityAttributes',
      type: 'object',
      'x-entity-label': {
        key: 'ACTIVITY_JOURNEY_TOOLBOX',
      },
      description: 'Model with toolbox activity attribute information.',
      properties: {
        entityMetadata: {
          type: 'object',
          'x-entity-presentation': {
            hidden: true,
          },
          required: ['status'],
          properties: {
            status: {
              type: 'string',
              'x-stoplight': {
                id: 'hq5783yg20ail',
              },
              description: 'status of toolbox activity in config tools.',
              enum: ['draft', 'published', 'archived'],
            },
            lastPublishedDate: {
              type: 'string',
              'x-stoplight': {
                id: 'kfhkepfj3lv0i',
              },
              description: 'Date and time activity was last published.',
              format: 'date-time',
            },
            lastUpdatedDate: {
              type: 'string',
              'x-stoplight': {
                id: '16b4y43cxpr6a',
              },
              format: 'date-time',
              description: 'date and time activity was last update.',
            },
            name: {
              type: 'string',
              'x-stoplight': {
                id: 'wv3ex58ycls9x',
              },
              description: 'Name of the entity.',
              'x-entity-localized': true,
            },
          },
        },
        metaData: {
          type: 'object',
          'x-entity-label': {
            key: 'ACTIVITY_META_DATA',
          },
          required: ['internalName', 'type'],
          properties: {
            internalName: {
              type: 'string',
              'x-entity-label': {
                key: 'INTERNAL_NAME',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_INTERNAL_NAME_HINT',
                },
              },
              description: 'Searchable within content auth, internal name.',
              allowEmpty: false,
              'x-entity-localized': true,
            },
            type: {
              type: 'string',
              'x-entity-label': {
                key: 'TYPE',
              },
              'x-entity-presentation': {
                hidden: true,
                enumLabels: {
                  enumControlPlaceholder: 'ACTIVITY_TYPE_SELECT_PLACEHOLDER',
                  'click-through': 'ACTIVITY_CLICK_THROUGH',
                  'no-touch': 'ACTIVITY_NO_TOUCH',
                },
              },
              enum: ['click-through', 'no-touch'],
              description: 'Type of activity.',
            },
            shortDescription: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_SHORT_DESCRIPTION',
              },
              description:
                'A short summary of campaign. Ensure characters do not exceed 150 character limit.',
              'x-entity-localized': true,
            },
            externalIdentifiers: {
              type: 'array',
              'x-entity-label': {
                key: 'EXTERNAL_IDENTIFIERS_LABEL',
              },
              items: {
                type: 'object',
                'x-entity-label': {
                  key: 'EXTERNAL_IDENTIFIER_LABEL',
                },
                properties: {
                  identifierName: {
                    type: 'string',
                    'x-entity-label': {
                      key: 'EXTERNAL_IDENTIFIER_NAME_LABEL',
                    },
                    allowEmpty: false,
                    'x-entity-localized': true,
                  },
                  identifierValue: {
                    type: 'string',
                    'x-entity-label': {
                      key: 'EXTERNAL_IDENTIFIER_VALUE_LABEL',
                    },
                    allowEmpty: false,
                    'x-entity-localized': true,
                  },
                },
                required: ['identifierName', 'identifierValue'],
              },
            },
          },
        },
        cardData: {
          type: 'object',
          'x-entity-label': {
            key: 'ACTIVITY_CARD_DATA',
          },
          required: ['title', 'priority', 'detailLevel'],
          properties: {
            title: {
              type: 'string',
              'x-entity-label': {
                key: 'TITLE',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_TITLE_HINT',
                },
              },
              description: 'This is the title that the user sees.',
              allowEmpty: false,
              'x-entity-localized': true,
            },
            tagline: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_TAGLINE',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_TAGLINE_HINT',
                },
              },
              description:
                'Displayed in the banner of an activity card, applicable to High- and Medium-priority activities only.',
              'x-entity-localized': true,
            },
            priority: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_PRIORITY_WEIGHTING',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_PRIORITY_WEIGHTING_HINT',
                },
                enumLabels: {
                  enumControlPlaceholder:
                    'ACTIVITY_PRIORITY_WEIGHTING_SELECT_PLACEHOLDER',
                  low: 'LOW',
                  medium: 'MEDIUM',
                  high: 'HIGH',
                },
              },
              enum: ['low', 'medium', 'high'],
              description: 'Activity priority.',
            },
            detailLevel: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_DISPLAY_WEIGHTING',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_DISPLAY_WEIGHTING_HINT',
                },
                enumLabels: {
                  enumControlPlaceholder:
                    'ACTIVITY_DISPLAY_WEIGHTING_SELECT_PLACEHOLDER',
                  simple: 'SIMPLE',
                  standard: 'STANDARD',
                  detailed: 'DETAILED',
                },
              },
              enum: ['simple', 'standard', 'detailed'],
              description:
                'Details of an activity, where simple has headline only, standard has headline and image and detailed has headline, image and description.',
            },
            activityImageId: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_IMAGE_ID',
              },
              'x-entity-asset': {
                type: 'image',
              },
              'x-entity-localized': true,
            },
          },
        },
        contentData: {
          type: 'object',
          'x-entity-label': {
            key: 'ACTIVITY_CONTENT_DATA',
          },
          properties: {
            hideProgressNumbers: {
              title: 'HideProgressNumbersComponentAttribute',
              type: 'boolean',
              'x-entity-label': {
                key: 'ACTIVITY_HIDE_PROGRESS_NUMBERS',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_HIDE_PROGRESS_NUMBERS_HINT',
                },
              },
              description:
                'Boolean configuration parameter responsible for hiding progress numbers in the activity.',
            },
            page: {
              type: 'array',
              'x-entity-label': {
                key: 'ACTIVITY_PAGES',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_PAGES_HINT',
                },
              },
              items: {
                title: 'ConfigActivityPage',
                type: 'object',
                'x-entity-label': {
                  key: 'ACTIVITY_PAGE',
                  propertyRef: '$.internalPageName',
                },
                'x-entity-presentation': {
                  hint: {
                    key: 'ACTIVITY_PAGE_HINT',
                  },
                },
                description: 'ActivityPage component.',
                properties: {
                  internalPageName: {
                    type: 'string',
                    'x-entity-label': {
                      key: 'ACTIVITY_INTERNAL_PAGE_NAME',
                    },
                    'x-entity-presentation': {
                      hint: {
                        key: 'ACTIVITY_INTERNAL_PAGE_NAME_HINT',
                      },
                    },
                    description: 'Page name.',
                    'x-entity-localized': true,
                  },
                  action: {
                    type: 'object',
                    'x-entity-label': {
                      key: 'ACTIVITY_CALL_TO_ACTION',
                    },
                    'x-entity-presentation': {
                      hint: {
                        key: 'ACTIVITY_CALL_TO_ACTION_HINT',
                      },
                    },
                    properties: {
                      url: {
                        type: 'string',
                        'x-entity-label': {
                          key: 'ACTIVITY_URL',
                        },
                        'x-entity-localized': true,
                      },
                      title: {
                        type: 'string',
                        'x-entity-label': {
                          key: 'ACTIVITY_TEXT',
                        },
                        'x-entity-localized': true,
                      },
                    },
                  },
                  components: {
                    type: 'array',
                    'x-entity-label': {
                      key: 'ACTIVITY_COMPONENTS',
                    },
                    'x-entity-presentation': {
                      hint: {
                        key: 'ACTIVITY_COMPONENTS_HINT',
                      },
                    },
                    items: {
                      discriminator: {
                        propertyName: 'componentType',
                      },
                      'x-entity-label': {
                        key: 'ACTIVITY_COMPONENTS',
                      },
                      'x-stoplight': {
                        id: 'c980o31q917c7',
                      },
                      oneOf: [
                        {
                          title: 'RichTextComponentAttribute',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_RICH_TEXT_COMPONENT',
                          },
                          description:
                            "Contains the Rich Text HTML for the component and it's type.",
                          properties: {
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_RICH_TEXT',
                              },
                              enum: ['rich-text'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_RICH_TEXT_COMPONENT_ATTRIBUTES',
                              },
                              properties: {
                                richText: {
                                  type: 'string',
                                  format: 'rich-text',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_RICH_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_RICH_TEXT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_LINK_COMPONENT',
                          },
                          'x-entity-presentation': {
                            hint: {
                              key: 'ACTIVITY_LINK_COMPONENT_HINT',
                            },
                          },
                          properties: {
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_LINK',
                              },
                              enum: ['link'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_LINK_COMPONENT_ATTRIBUTES',
                              },
                              properties: {
                                iconUrl: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_ICON_URL',
                                  },
                                },
                                url: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_LINK_URL',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_LINK_URL_HINT',
                                    },
                                  },
                                  allowEmpty: false,
                                  'x-entity-localized': true,
                                },
                                title: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_LINK_TITLE',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_LINK_TITLE_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                ctaText: {
                                  type: 'string',
                                  description:
                                    'The text that will show up as a cta to the link.',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_CTA_TEXT',
                                  },
                                  'x-entity-localized': true,
                                },
                              },
                              required: ['url'],
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'HeaderComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_HEADER_COMPONENT',
                          },
                          description:
                            "The HeaderComponent is meant to be used as the first component of the first page of a Toolbox Activity, and it'll display an image on the top of the first page.",
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_HEADER',
                              },
                              enum: ['header'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_HEADER_COMPONENT_ATTRIBUTES',
                              },
                              'x-entity-presentation': {
                                hint: {
                                  key: 'ACTIVITY_HEADER_COMPONENT_ATTRIBUTES_HINT',
                                },
                              },
                              properties: {
                                imageUrl: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_HEADER_IMAGE_URL',
                                  },
                                },
                                progressPercentage: {
                                  type: 'integer',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_HEADER_PROGRESS_PERCENTAGE',
                                  },
                                  'x-entity-presentation': {
                                    hidden: true,
                                    banner: {
                                      title: {
                                        key: 'ACTIVITY_HEADER_PROGRESS_PERCENTAGE_BANNER_TITLE',
                                      },
                                      description: {
                                        key: 'ACTIVITY_HEADER_PROGRESS_PERCENTAGE_BANNER_DESCRIPTION',
                                      },
                                      status: 'info',
                                    },
                                  },
                                  maximum: 100,
                                  minimum: 0,
                                },
                                campaignName: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_HEADER_CAMPAIGN_NAME',
                                  },
                                  'x-entity-localized': true,
                                },
                                alt: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-localized': true,
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'BlockQuoteComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_BLOCK_QUOTE_COMPONENT',
                          },
                          description:
                            'The BlockQuote component is meant to represent the BlockQuote HTML tag, which used to be part of the RichText component. Instead of using the BlockQuote tag inside the RichText component, a BlockQuoteComponent should be added right after the RichTextComponent.',
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_BLOCK_QUOTE',
                              },
                              enum: ['block-quote'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_BLOCK_QUOTE_COMPONENT_ATTRIBUTES',
                              },
                              properties: {
                                text: {
                                  type: 'string',
                                  format: 'rich-text',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BLOCK_QUOTE_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_BLOCK_QUOTE_TEXT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'ImageComponentAttribute',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_IMAGE_COMPONENT',
                          },
                          description:
                            "Contains the URL pointing to the image asset for the component and it's type.",
                          properties: {
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_IMAGE',
                              },
                              enum: ['image'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_IMAGE_COMPONENT_ATTRIBUTES',
                              },
                              properties: {
                                imageUrl: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_IMAGE_URL',
                                  },
                                },
                                alt: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_IMAGE_ALT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_IMAGE_ALT_HINT',
                                    },
                                  },
                                  description: 'Alternate text',
                                  'x-entity-localized': true,
                                },
                                caption: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_IMAGE_CAPTION',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_IMAGE_CAPTION_HINT',
                                    },
                                  },
                                  description: 'Describes the image',
                                  'x-entity-localized': true,
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'BannerComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_BANNER_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_BANNER',
                              },
                              enum: ['banner'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_BANNER_COMPONENT_ATTRIBUTES',
                              },
                              properties: {
                                title: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BANNER_TITLE',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_BANNER_TITLE_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                iconUrl: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_ICON_URL',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_BANNER_ICON_URL_HINT',
                                    },
                                  },
                                },
                                type: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BANNER_TYPE',
                                  },
                                  'x-entity-presentation': {
                                    banner: {
                                      title: {
                                        key: 'ACTIVITY_BANNER_TYPE_PRESENTATION_TITLE',
                                      },
                                      description: {
                                        key: 'ACTIVITY_BANNER_TYPE_PRESENTATION_DESCRIPTION',
                                      },
                                      status: 'info',
                                    },
                                    enumLabels: {
                                      enumControlPlaceholder:
                                        'ACTIVITY_BANNER_TYPE_PLACEHOLDER',
                                      primary: 'PRIMARY',
                                      secondary: 'SECONDARY',
                                      subtle: 'SUBTLE',
                                    },
                                  },
                                  enum: ['primary', 'secondary', 'subtle'],
                                },
                                richText: {
                                  type: 'string',
                                  format: 'rich-text',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BANNER_RICH_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_BANNER_RICH_TEXT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                              },
                              required: ['type'],
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'FullPageHighlightComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT',
                              },
                              enum: ['full-page-highlight'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_COMPONENT_ATTRIBUTES',
                              },
                              properties: {
                                title: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_TITLE',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_TITLE_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                subtitle: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_SUBTITLE',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_SUBTITLE_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                highlightImage: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_HIGHLIGHT_IMAGE',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_HIGHLIGHT_IMAGE_HINT',
                                    },
                                  },
                                },
                                highlightText: {
                                  type: 'string',
                                  format: 'rich-text',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_HIGHLIGHT_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_HIGHLIGHT_TEXT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                backgroundImage: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_BACKGROUND_IMAGE',
                                  },
                                },
                                backgroundColor: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_BACKGROUND_COLOR',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_FULL_PAGE_HIGHLIGHT_BACKGROUND_COLOR_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'ListComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_LIST_COMPONENT',
                          },
                          description:
                            'The List component is meant to represent the UL/LI HTML tags, which used to be part of the RichText component. Instead of using the UL/LI tags inside the richtext component, a ListComponent should be added right after the richtextComponent in order to display a List.',
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'LIST',
                              },
                              enum: ['list'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_LIST_COMPONENT_ATTRIBUTES',
                              },
                              'x-entity-presentation': {
                                hint: {
                                  key: 'ACTIVITY_LIST_COMPONENT_ATTRIBUTES_HINT',
                                },
                              },
                              properties: {
                                list: {
                                  type: 'array',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_LIST_ATTRIBUTE',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_LIST_ATTRIBUTE_HINT',
                                    },
                                    arrayAddLabel:
                                      'ACTIVITY_LIST_ARRAY_ADD_LABEL',
                                  },
                                  items: {
                                    type: 'string',
                                    format: 'rich-text',
                                    'x-entity-presentation': {
                                      hideLabel: true,
                                    },
                                  },
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'BadgeComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_BADGE_COMPONENT',
                          },
                          required: ['componentType', 'componentAttributes'],
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'BADGE',
                              },
                              enum: ['badge'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_BADGE_COMPONENT_ATTRIBUTES',
                              },
                              properties: {
                                style: {
                                  type: 'string',
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                text: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BADGE_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_BADGE_TEXT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_BADGE_ALT_TEXT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                iconUrl: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_ICON_URL',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_BADGE_ICON_URL_HINT',
                                    },
                                  },
                                },
                                placeholderValueModifiers: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BADGE_PLACEHOLDER_MODIFIER',
                                  },
                                  properties: {
                                    dateFormatter: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_BADGE_DATE_FORMATTER',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_BADGE_DATE_FORMATTER_HINT',
                                        },
                                      },
                                      enum: ['long', 'short', 'full', 'medium'],
                                      default: 'medium',
                                    },
                                  },
                                },
                                styleModifiers: {
                                  title: 'StyleModifiers',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BADGE_STYLE_MODIFIERS',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_BADGE_STYLE_MODIFIERS_HINT',
                                    },
                                  },
                                  properties: {
                                    backgroundColor: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'BACKGROUND_COLOR',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_BADGE_STYLE_MODIFIERS_BACKGROUND_COLOR_HINT',
                                        },
                                        enumLabels: {
                                          enumControlPlaceholder:
                                            'ACTIVITY_BADGE_STYLE_MODIFIERS_PLACEHOLDER',
                                          'decorative-brand-primary-pastel':
                                            'ACTIVITY_BADGE_STYLE_MODIFIERS_BACKGROUND_COLOR_OPTION_1',
                                          'decorative-brand-secondary-pastel':
                                            'ACTIVITY_BADGE_STYLE_MODIFIERS_BACKGROUND_COLOR_OPTION_2',
                                          'decorative-brand-tertiary-pastel':
                                            'ACTIVITY_BADGE_STYLE_MODIFIERS_BACKGROUND_COLOR_OPTION_3',
                                        },
                                      },
                                      enum: [
                                        'decorative-brand-primary-pastel',
                                        'decorative-brand-secondary-pastel',
                                        'decorative-brand-tertiary-pastel',
                                      ],
                                    },
                                    horizontalAlignment: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'HORIZONTAL_ALIGNMENT',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_BADGE_STYLE_MODIFIERS_HORIZONTAL_ALIGNMENT_HINT',
                                        },
                                        enumLabels: {
                                          enumControlPlaceholder:
                                            'ACTIVITY_BADGE_STYLE_MODIFIERS_PLACEHOLDER',
                                          start: 'START',
                                          center: 'CENTER',
                                          end: 'END',
                                        },
                                      },
                                      enum: ['start', 'center', 'end'],
                                    },
                                    borderRadius: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'BORDER_RADIUS',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_BADGE_STYLE_MODIFIERS_BORDER_RADIUS_HINT',
                                        },
                                        enumLabels: {
                                          enumControlPlaceholder:
                                            'ACTIVITY_BADGE_STYLE_MODIFIERS_PLACEHOLDER',
                                          pill: 'PILL',
                                          medium: 'MEDIUM',
                                        },
                                      },
                                      enum: ['pill', 'medium'],
                                    },
                                  },
                                },
                                componentStorageSource: {
                                  title: 'ObservationStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_OBSERVATION_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_OBSERVATION',
                                      },
                                      enum: ['fhir-observation'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        metric: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'METRIC',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        event: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'EVENT',
                                          },
                                          description:
                                            'valueConcept field represents the FHIR observation value, if the value is a CodeableConcept',
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                        },
                        {
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_MULTI_SELECT_INPUT_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_MULTI_SELECT_INPUT',
                              },
                              enum: ['multiple-select-input'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_MULTI_SELECT_INPUT_ATTRIBUTES',
                              },
                              required: ['componentStorageSource'],
                              properties: {
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-localized': true,
                                },
                                answerOptions: {
                                  type: 'array',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_MULTI_SELECT_INPUT_ANSWER_OPTIONS',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_MULTI_SELECT_INPUT_ANSWER_OPTIONS_HINT',
                                    },
                                    arrayAddLabel:
                                      'ACTIVITY_MULTI_SELECT_INPUT_ANSWER_OPTIONS_ADD_LABEL',
                                  },
                                  items: {
                                    type: 'object',
                                    'x-entity-label': {
                                      key: 'ACTIVITY_MULTI_SELECT_INPUT_ANSWER_OPTIONS_ITEM',
                                      propertyRef: '$.text',
                                    },
                                    properties: {
                                      text: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'ACTIVITY_TEXT',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_MULTI_SELECT_INPUT_TEXT_HINT',
                                          },
                                        },
                                        'x-entity-localized': true,
                                      },
                                      subText: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'ACTIVITY_SUB_TEXT',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_MULTI_SELECT_INPUT_SUB_TEXT_HINT',
                                          },
                                        },
                                        'x-entity-localized': true,
                                      },
                                      value: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'VALUE',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_MULTI_SELECT_INPUT_VALUE_HINT',
                                          },
                                          banner: {
                                            status: 'info',
                                            title: {
                                              key: 'INFO',
                                            },
                                            description: {
                                              key: 'ACTIVITY_MULTI_SELECT_INPUT_VALUE_BANNER_DESCRIPTION',
                                            },
                                          },
                                        },
                                        'x-entity-localized': true,
                                      },
                                      iconUrl: {
                                        type: 'string',
                                        format: 'uri-reference',
                                        'x-entity-asset': {
                                          type: 'image',
                                        },
                                        'x-entity-label': {
                                          key: 'ACTIVITY_ICON_URL',
                                        },
                                      },
                                      isSelected: {
                                        type: 'boolean',
                                        default: false,
                                        'x-entity-presentation': {
                                          hidden: true,
                                        },
                                      },
                                      code: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'CODE',
                                        },
                                        'x-entity-localized': true,
                                      },
                                      enableFreeText: {
                                        type: 'boolean',
                                        default: false,
                                        'x-entity-label': {
                                          key: 'ACTIVITY_MULTI_SELECT_INPUT_ENABLE_FREE_TEXT',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_MULTI_SELECT_INPUT_ENABLE_FREE_TEXT_HINT',
                                          },
                                        },
                                      },
                                      inputType: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'ACTIVITY_MULTI_SELECT_INPUT_TYPE',
                                        },
                                        'x-entity-presentation': {
                                          hidden: true,
                                          enumLabels: {
                                            enumControlPlaceholder:
                                              'ACTIVITY_MULTI_SELECT_PLACEHOLDER',
                                            string: 'STRING',
                                            integer: 'INTEGER',
                                            decimal: 'DECIMAL',
                                            coding: 'CODING',
                                            date: 'DATE',
                                            time: 'TIME',
                                          },
                                        },
                                        enum: [
                                          'string',
                                          'integer',
                                          'decimal',
                                          'coding',
                                          'date',
                                          'time',
                                        ],
                                      },
                                      freeTextValidation: {
                                        type: 'object',
                                        'x-entity-label': {
                                          key: 'ACTIVITY_INPUT_FREE_TEXT_VALIDATION',
                                        },
                                        properties: {
                                          inputType: {
                                            type: 'string',
                                            'x-entity-label': {
                                              key: 'ACTIVITY_MULTI_SELECT_INPUT_TYPE',
                                            },
                                            'x-entity-presentation': {
                                              enumLabels: {
                                                enumControlPlaceholder:
                                                  'ACTIVITY_MULTI_SELECT_PLACEHOLDER',
                                                string: 'STRING',
                                                integer: 'INTEGER',
                                              },
                                            },
                                            enum: ['', 'string', 'integer'],
                                          },
                                          placeholder: {
                                            type: 'string',
                                            'x-entity-label': {
                                              key: 'ACTIVITY_INPUT_PLACEHOLDER',
                                            },
                                            'x-entity-presentation': {
                                              hint: {
                                                key: 'ACTIVITY_INPUT_PLACEHOLDER_HINT',
                                              },
                                            },
                                            'x-entity-localized': true,
                                          },
                                          characterLimit: {
                                            type: 'integer',
                                            default: 0,
                                            'x-entity-label': {
                                              key: 'ACTIVITY_MULTI_SELECT_CHARACTER_LIMIT',
                                            },
                                            'x-entity-presentation': {
                                              hint: {
                                                key: 'ACTIVITY_MULTI_SELECT_CHARACTER_LIMIT_HINT',
                                              },
                                            },
                                          },
                                          minimumValue: {
                                            type: 'integer',
                                            default: 0,
                                            'x-entity-label': {
                                              key: 'ACTIVITY_MULTI_SELECT_MIN_VALUE',
                                            },
                                            'x-entity-presentation': {
                                              hint: {
                                                key: 'ACTIVITY_MULTI_SELECT_VALUE_HINT',
                                              },
                                            },
                                          },
                                          maximumValue: {
                                            type: 'integer',
                                            default: 0,
                                            'x-entity-label': {
                                              key: 'ACTIVITY_MULTI_SELECT_MAX_VALUE',
                                            },
                                            'x-entity-presentation': {
                                              hint: {
                                                key: 'ACTIVITY_MULTI_SELECT_VALUE_HINT',
                                              },
                                            },
                                          },
                                        },
                                        required: [
                                          'characterLimit',
                                          'minimumValue',
                                          'maximumValue',
                                        ],
                                      },
                                      id: {
                                        type: 'string',
                                        'x-entity-presentation': {
                                          hidden: true,
                                        },
                                        allowEmpty: false,
                                      },
                                      exclusive: {
                                        type: 'boolean',
                                        default: false,
                                        'x-entity-label': {
                                          key: 'ACTIVITY_MULTI_SELECT_EXCLUSIVE',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_MULTI_SELECT_EXCLUSIVE_HINT',
                                          },
                                        },
                                        description:
                                          'if this field set to true then the FE will unselect all other selected options if this option is selected',
                                      },
                                    },
                                    required: [
                                      'isSelected',
                                      'enableFreeText',
                                      'id',
                                      'exclusive',
                                    ],
                                  },
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    minimumSelectionRequired: {
                                      default: 0,
                                      type: 'integer',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_MULTI_SELECT_MIN_SELECTION_REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_MULTI_SELECT_MIN_SELECTION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                    maximumSelectionRequired: {
                                      type: 'integer',
                                      default: 0,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_MULTI_SELECT_MAX_SELECTION_REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_MULTI_SELECT_MAX_SELECTION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                    required: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                    customWarning: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING',
                                      },
                                      'x-entity-localized': true,
                                    },
                                  },
                                  required: [
                                    'minimumSelectionRequired',
                                    'maximumSelectionRequired',
                                    'required',
                                  ],
                                },
                                prepopulate: {
                                  type: 'boolean',
                                  default: true,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PRE_POPULATE',
                                  },
                                },
                                displayAsDropdown: {
                                  type: 'boolean',
                                  default: false,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_DISPLAY_AS_DROPDOWN',
                                  },
                                },
                                componentStorageSource: {
                                  title: 'QuestionnaireStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_QUESTIONNAIRE_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_QUESTIONNAIRE',
                                      },
                                      enum: ['fhir-questionnaire'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        questionnaireReference: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_QUESTIONNAIRE_REFERENCE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        linkId: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_LINK_ID',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'ProgressTrackingComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_PROGRESS_TRACKING_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_PROGRESS_TRACKING',
                              },
                              enum: ['progress-tracking'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_PROGRESS_TRACKING_COMPONENT_ATTRIBUTES',
                              },
                              'x-entity-presentation': {
                                hint: {
                                  key: 'ACTIVITY_PROGRESS_TRACKING_COMPONENT_ATTRIBUTES_HINT',
                                },
                                banner: {
                                  status: 'info',
                                  title: {
                                    key: 'ACTIVITY_PROGRESS_TRACKING_BANNER_TITLE',
                                  },
                                  description: {
                                    key: 'ACTIVITY_PROGRESS_TRACKING_BANNER_DESCRIPTION',
                                  },
                                },
                              },
                              required: [
                                'goal',
                                'currentProgress',
                                'progressPercentage',
                                'componentStorageSource',
                              ],
                              properties: {
                                unit: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_PROGRESS_TRACKING_UNIT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_PROGRESS_TRACKING_UNIT_HINT',
                                    },
                                  },
                                  description: 'User Facing Metric.',
                                  'x-entity-localized': true,
                                },
                                metric: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'METRIC',
                                  },
                                  description:
                                    'Internal code used for storing the metric.',
                                  'x-entity-localized': true,
                                },
                                goal: {
                                  type: 'integer',
                                  default: 0,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_PROGRESS_TRACKING_GOAL',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_PROGRESS_TRACKING_GOAL_HINT',
                                    },
                                  },
                                },
                                currentProgress: {
                                  type: 'number',
                                  default: 0,
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                progressPercentage: {
                                  type: 'integer',
                                  default: 0,
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                inputMethod: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_PROGRESS_TRACKING_INPUT_METHOD',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_PROGRESS_TRACKING_INPUT_METHOD_HINT',
                                    },
                                    enumLabels: {
                                      enumControlPlaceholder:
                                        'ACTIVITY_PROGRESS_TRACKING_INPUT_METHOD_PLACEHOLDER',
                                      'circular-bar':
                                        'ACTIVITY_PROGRESS_TRACKING_CIRCULAR_BAR',
                                      'horizontal-bar':
                                        'ACTIVITY_PROGRESS_TRACKING_HORIZONTAL_BAR',
                                      button:
                                        'ACTIVITY_PROGRESS_TRACKING_BUTTON',
                                    },
                                  },
                                  enum: [
                                    'button',
                                    'horizontal-bar',
                                    'circular-bar',
                                  ],
                                },
                                prepopulate: {
                                  type: 'boolean',
                                  default: true,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PRE_POPULATE',
                                  },
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    allowCompletionBellowGoal: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-presentation': {
                                        hidden: true,
                                      },
                                    },
                                    allowUserOverwrite: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-presentation': {
                                        hidden: true,
                                      },
                                    },
                                    currentProgressLimit: {
                                      type: 'integer',
                                      default: 0,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_PROGRESS_TRACKING_CURRENT_PROGRESS_LIMIT',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_PROGRESS_TRACKING_CURRENT_PROGRESS_LIMIT_HINT',
                                        },
                                      },
                                    },
                                    customWarning: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING_HINT',
                                        },
                                      },
                                      'x-entity-localized': true,
                                    },
                                    updateWindowInDays: {
                                      type: 'integer',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_PROGRESS_TRACKING_UPDATE_WINDOW_IN_DAYS',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_PROGRESS_TRACKING_UPDATE_WINDOW_IN_DAYS_HINT',
                                        },
                                        banner: {
                                          status: 'info',
                                          title: {
                                            key: 'ACTIVITY_PROGRESS_TRACKING_UPDATE_WINDOW_IN_DAYS_BANNER_TITLE',
                                          },
                                          description: {
                                            key: 'ACTIVITY_PROGRESS_TRACKING_UPDATE_WINDOW_IN_DAYS_BANNER_DESCRIPTION',
                                          },
                                        },
                                      },
                                      description:
                                        'An offset number of days defined by the content editor. If the Update Window is less than the Activity End Date, the Editable field will be set to true.',
                                    },
                                  },
                                  required: [
                                    'allowCompletionBellowGoal',
                                    'allowUserOverwrite',
                                    'currentProgressLimit',
                                  ],
                                },
                                editButtonLabel: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_PROGRESS_TRACKING_EDIT_BUTTON_LABEL',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_PROGRESS_TRACKING_EDIT_BUTTON_LABEL_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                componentStorageSource: {
                                  title: 'ObservationStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_OBSERVATION_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_OBSERVATION',
                                      },
                                      enum: ['fhir-observation'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        metric: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'METRIC',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        event: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'EVENT',
                                          },
                                          description:
                                            'valueConcept field represents the FHIR observation value, if the value is a CodeableConcept',
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'IntegerInputComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_INTEGER_INPUT_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_INTEGER_INPUT',
                              },
                              enum: ['integer-input'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_INTEGER_INPUT_COMPONENT_ATTRIBUTES',
                              },
                              required: [
                                'prepopulate',
                                'componentStorageSource',
                              ],
                              properties: {
                                prefix: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PREFIX',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_PREFIX_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                suffix: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_SUFFIX',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_SUFFIX_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                answer: {
                                  type: 'integer',
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                inputMethod: {
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                  type: 'string',
                                  enum: ['manual'],
                                },
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_ALT_TEXT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                placeholder: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PLACEHOLDER',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_PLACEHOLDER_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                unit: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'UNIT',
                                  },
                                  'x-entity-localized': true,
                                },
                                prepopulate: {
                                  type: 'boolean',
                                  default: true,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PRE_POPULATE',
                                  },
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    minimumValue: {
                                      type: 'integer',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_MIN_VALUE',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_MIN_VALUE_HINT',
                                        },
                                      },
                                    },
                                    maximumValue: {
                                      type: 'integer',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_MAX_VALUE',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_MAX_VALUE_HINT',
                                        },
                                      },
                                    },
                                    required: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-label': {
                                        key: 'REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                    customWarning: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING_HINT',
                                        },
                                      },
                                      'x-entity-localized': true,
                                    },
                                  },
                                  required: ['required'],
                                },
                                componentStorageSource: {
                                  title: 'QuestionnaireStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_QUESTIONNAIRE_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_QUESTIONNAIRE',
                                      },
                                      enum: ['fhir-questionnaire'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        questionnaireReference: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_QUESTIONNAIRE_REFERENCE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        linkId: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_LINK_ID',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_DATE_INPUT_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_DATE_INPUT',
                              },
                              enum: ['date-input'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_DATE_INPUT_COMPONENT_ATTRIBUTES',
                              },
                              required: ['componentStorageSource'],
                              properties: {
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-localized': true,
                                },
                                answer: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ANSWER',
                                  },
                                  format: 'date-time',
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                dateInputMethod: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_DATE_INPUT_DATE_INPUT_METHOD',
                                  },
                                  'x-entity-presentation': {
                                    enumLabels: {
                                      enumControlPlaceholder:
                                        'ACTIVITY_DATE_INPUT_METHOD_PLACEHOLDER',
                                      'date-pick':
                                        'ACTIVITY_DATE_INPUT_METHOD_DATE_PICK',
                                      'month-year':
                                        'ACTIVITY_DATE_INPUT_METHOD_MONTH_YEAR',
                                    },
                                  },
                                  enum: ['date-pick', 'month-year'],
                                },
                                placeholder: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PLACEHOLDER',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_PLACEHOLDER_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                prepopulate: {
                                  type: 'boolean',
                                  default: true,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PRE_POPULATE',
                                  },
                                },
                                additionalDateOptions: {
                                  type: 'array',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_DATE_INPUT_ADDITIONAL_DATE_OPTIONS',
                                  },
                                  'x-entity-presentation': {
                                    arrayAddLabel:
                                      'ACTIVITY_DATE_INPUT_ADDITIONAL_DATE_OPTIONS_ADD_LABEL',
                                    hint: {
                                      key: 'ACTIVITY_DATE_INPUT_ADDITIONAL_DATE_OPTIONS_HINT',
                                    },
                                  },
                                  items: {
                                    type: 'object',
                                    'x-entity-label': {
                                      key: 'ACTIVITY_DATE_INPUT_ADDITIONAL_DATE_OPTIONS_ARRAY_ITEM',
                                    },
                                    properties: {
                                      text: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'ACTIVITY_TEXT',
                                        },
                                        'x-entity-localized': true,
                                      },
                                      value: {
                                        type: 'integer',
                                        default: 0,
                                        'x-entity-label': {
                                          key: 'VALUE',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_DATE_INPUT_ADDITIONAL_DATE_OPTIONS_VALUE_HINT',
                                          },
                                        },
                                      },
                                      unit: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'UNIT',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_DATE_INPUT_ADDITIONAL_DATE_OPTIONS_UNIT_HINT',
                                          },
                                        },
                                        'x-entity-localized': true,
                                      },
                                      isSelected: {
                                        type: 'boolean',
                                        default: false,
                                        'x-entity-presentation': {
                                          hidden: true,
                                        },
                                      },
                                      id: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'ID',
                                        },
                                        'x-entity-presentation': {
                                          hidden: true,
                                        },
                                        allowEmpty: false,
                                      },
                                      code: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'CODE',
                                        },
                                        'x-entity-localized': true,
                                      },
                                    },
                                    required: ['value', 'isSelected', 'id'],
                                  },
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    maximumDate: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_DATE_OBJECT_MAX_DATE',
                                      },
                                      properties: {
                                        maxDate: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_DATE_INPUT_MAX_DATE',
                                          },
                                          format: 'date-time',
                                        },
                                        useCurrentDate: {
                                          type: 'boolean',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_USE_CURRENT_DATE',
                                          },
                                          'x-entity-presentation': {
                                            hint: {
                                              key: 'ACTIVITY_CURRENT_DATE_HINT',
                                            },
                                          },
                                        },
                                      },
                                    },
                                    minimumDate: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_DATE_OBJECT_MIN_DATE',
                                      },
                                      properties: {
                                        minDate: {
                                          type: 'string',
                                          format: 'date-time',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_DATE_INPUT_MIN_DATE',
                                          },
                                        },
                                        useCurrentDate: {
                                          type: 'boolean',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_USE_CURRENT_DATE',
                                          },
                                          'x-entity-presentation': {
                                            hint: {
                                              key: 'ACTIVITY_CURRENT_DATE_HINT',
                                            },
                                          },
                                        },
                                      },
                                    },
                                    required: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                    customWarning: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING_HINT',
                                        },
                                      },
                                      'x-entity-localized': true,
                                    },
                                  },
                                  required: ['required'],
                                },
                                componentStorageSource: {
                                  title: 'QuestionnaireStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_QUESTIONNAIRE_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_QUESTIONNAIRE',
                                      },
                                      enum: ['fhir-questionnaire'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        questionnaireReference: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_QUESTIONNAIRE_REFERENCE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        linkId: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_LINK_ID',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_STRING_INPUT_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_STRING_INPUT',
                              },
                              enum: ['string-input'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_STRING_INPUT_COMPONENT_ATTRIBUTES',
                              },
                              required: ['componentStorageSource'],
                              properties: {
                                prefix: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PREFIX',
                                  },
                                  'x-entity-localized': true,
                                },
                                suffix: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_SUFFIX',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_SUFFIX_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                answer: {
                                  type: 'string',
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                inputMethod: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_INPUT_METHOD',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_STRING_INPUT_INPUT_METHOD_HINT',
                                    },
                                    enumLabels: {
                                      enumControlPlaceholder:
                                        'ACTIVITY_STRING_PLACEHOLDER',
                                      line: 'LINE',
                                      box: 'BOX',
                                    },
                                  },
                                  enum: ['line', 'box'],
                                },
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-localized': true,
                                },
                                placeholder: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PLACEHOLDER',
                                  },
                                  'x-entity-localized': true,
                                },
                                prepopulate: {
                                  type: 'boolean',
                                  default: true,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PRE_POPULATE',
                                  },
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    characterLimit: {
                                      type: 'integer',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STRING_CHARACTER_LIMIT',
                                      },
                                    },
                                    required: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                    customWarning: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING',
                                      },
                                      'x-entity-localized': true,
                                    },
                                  },
                                  required: ['required'],
                                },
                                componentStorageSource: {
                                  title: 'QuestionnaireStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_QUESTIONNAIRE_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_QUESTIONNAIRE',
                                      },
                                      enum: ['fhir-questionnaire'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        questionnaireReference: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_QUESTIONNAIRE_REFERENCE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        linkId: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_LINK_ID',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'DecimalInputComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_DECIMAL_INPUT_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_DECIMAL_INPUT',
                              },
                              enum: ['decimal-input'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_DECIMAL_INPUT_COMPONENT_ATTRIBUTES',
                              },
                              required: ['componentStorageSource'],
                              properties: {
                                prefix: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PREFIX',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_PREFIX_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                suffix: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_SUFFIX',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_SUFFIX_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                answer: {
                                  type: 'number',
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_ALT_TEXT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                placeholder: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PLACEHOLDER',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_PLACEHOLDER_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                unit: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'UNIT',
                                  },
                                  'x-entity-localized': true,
                                },
                                prepopulate: {
                                  type: 'boolean',
                                  default: true,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PRE_POPULATE',
                                  },
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    minimumValue: {
                                      type: 'number',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_MIN_VALUE',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_MIN_VALUE_HINT',
                                        },
                                      },
                                    },
                                    maximumValue: {
                                      type: 'number',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_MAX_VALUE',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_MAX_VALUE_HINT',
                                        },
                                      },
                                    },
                                    required: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-label': {
                                        key: 'REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                    customWarning: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_CUSTOM_WARNING_HINT',
                                        },
                                      },
                                      'x-entity-localized': true,
                                    },
                                  },
                                  required: ['required'],
                                },
                                componentStorageSource: {
                                  title: 'QuestionnaireStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_QUESTIONNAIRE_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_QUESTIONNAIRE',
                                      },
                                      enum: ['fhir-questionnaire'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        questionnaireReference: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_QUESTIONNAIRE_REFERENCE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        linkId: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_LINK_ID',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'RewardsHeaderComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_REWARDS_HEADER_COMPONENT',
                          },
                          description:
                            "The RewardsHeaderComponent is meant to be used as the first component of the first page of a Toolbox Activity Rewards Campaign, and it'll display an image on the top of the first page.",
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_REWARDS_HEADER',
                              },
                              enum: ['rewards-header'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_REWARDS_HEADER_COMPONENT_ATTRIBUTES',
                              },
                              'x-entity-presentation': {
                                hint: {
                                  key: 'ACTIVITY_REWARDS_HEADER_COMPONENT_ATTRIBUTES_HINT',
                                },
                              },
                              properties: {
                                imageUrl: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_HEADER_IMAGE_URL',
                                  },
                                },
                                campaignName: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_HEADER_CAMPAIGN_NAME',
                                  },
                                  'x-entity-localized': true,
                                },
                                alt: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_HEADER_ALT',
                                  },
                                  'x-entity-localized': true,
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'VideoComponentAttributes',
                          type: 'object',
                          description:
                            'The VideoComponent is a foundational component which allows the user to watch a video inside an activity via an in-built player without the need of being redirected to a different web page.',
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_VIDEO',
                              },
                              enum: ['video'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              required: ['videoUrl', 'altText'],
                              'x-entity-label': {
                                key: 'ACTIVITY_VIDEO_COMPONENT_ATTRIBUTES',
                              },
                              properties: {
                                videoUrl: {
                                  type: 'string',
                                  format: 'url',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_VIDEO_VIDEO_URL',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_VIDEO_VIDEO_URL_HINT',
                                    },
                                  },
                                  description: 'An URL to a video file',
                                  allowEmpty: false,
                                },
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_VIDEO_ALT_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_VIDEO_ALT_TEXT_HINT',
                                    },
                                  },
                                  description:
                                    'A brief text describing the video',
                                  allowEmpty: false,
                                  'x-entity-localized': true,
                                },
                                thumbnailUrl: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_VIDEO_THUMBNAIL_URL',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_VIDEO_THUMBNAIL_URL_HINT',
                                    },
                                  },
                                  description:
                                    'A URL for an image file which will serve as thumbnail for the video',
                                  'x-entity-localized': true,
                                },
                                transcript: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_VIDEO_TRANSCRIPT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_VIDEO_TRANSCRIPT_HINT',
                                    },
                                  },
                                  description:
                                    'A url to the the transcription of the video content',
                                  'x-entity-localized': true,
                                },
                                subtitles: {
                                  type: 'array',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_VIDEO_SUBTITLES',
                                  },
                                  'x-entity-presentation': {
                                    arrayAddLabel:
                                      'ACTIVITY_VIDEO_SUBTITLES_ADD_LABEL',
                                    hint: {
                                      key: 'ACTIVITY_VIDEO_SUBTITLES_HINT',
                                    },
                                  },
                                  description:
                                    'An array of subtitle objects with the available subtitles for the video ',
                                  items: {
                                    'x-entity-label': {
                                      key: 'ACTIVITY_VIDEO_SUBTITLES_ARRAY_ITEM',
                                      propertyRef: '$.label',
                                    },
                                    type: 'object',
                                    required: ['label', 'url'],
                                    properties: {
                                      label: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'ACTIVITY_VIDEO_SUBTITLES_LABEL',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_VIDEO_SUBTITLES_LABEL_HINT',
                                          },
                                        },
                                        description:
                                          'The subtitle language name',
                                        allowEmpty: false,
                                        'x-entity-localized': true,
                                      },
                                      url: {
                                        type: 'string',
                                        'x-entity-label': {
                                          key: 'ACTIVITY_VIDEO_SUBTITLES_URL',
                                        },
                                        'x-entity-presentation': {
                                          hint: {
                                            key: 'ACTIVITY_VIDEO_SUBTITLES_URL_HINT',
                                          },
                                        },
                                        description:
                                          'An url pointing to the subtitle file',
                                        allowEmpty: false,
                                        'x-entity-localized': true,
                                      },
                                    },
                                  },
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                          'x-entity-label': {
                            key: 'ACTIVITY_VIDEO_COMPONENT',
                          },
                          'x-entity-presentation': {
                            hint: {
                              key: 'ACTIVITY_VIDEO_COMPONENT_HINT',
                            },
                          },
                        },
                        {
                          title: 'PersonHeightInputComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_PERSON_HEIGHT_INPUT_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_PERSON_HEIGHT_INPUT',
                              },
                              enum: ['person-height-input'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_PERSON_HEIGHT_INPUT_COMPONENT_ATTRIBUTES',
                              },
                              required: ['componentStorageSource'],
                              properties: {
                                answer: {
                                  type: 'number',
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-localized': true,
                                },
                                suffix: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_SUFFIX',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_SUFFIX_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                placeholder: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PLACEHOLDER',
                                  },
                                  'x-entity-localized': true,
                                },
                                unit: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'UNIT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_PERSON_HEIGHT_INPUT_UNIT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                prepopulate: {
                                  type: 'boolean',
                                  default: true,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PRE_POPULATE',
                                  },
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    minimumValue: {
                                      type: 'number',
                                      default: 0,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_MULTI_SELECT_MIN_VALUE',
                                      },
                                    },
                                    maximumValue: {
                                      type: 'number',
                                      default: 0,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_MULTI_SELECT_MAX_VALUE',
                                      },
                                    },
                                    required: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                  },
                                  required: [
                                    'minimumValue',
                                    'maximumValue',
                                    'required',
                                  ],
                                },
                                componentStorageSource: {
                                  title: 'QuestionnaireStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_QUESTIONNAIRE_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_QUESTIONNAIRE',
                                      },
                                      enum: ['fhir-questionnaire'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        questionnaireReference: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_QUESTIONNAIRE_REFERENCE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        linkId: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_LINK_ID',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          title: 'PersonWeightInputComponentAttributes',
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_PERSON_WEIGHT_INPUT_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_PERSON_WEIGHT_INPUT',
                              },
                              enum: ['person-weight-input'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_PERSON_WEIGHT_INPUT_COMPONENT_ATTRIBUTES',
                              },
                              required: ['componentStorageSource'],
                              properties: {
                                answer: {
                                  type: 'number',
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                altText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ALT_TEXT',
                                  },
                                  'x-entity-localized': true,
                                },
                                suffix: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_SUFFIX',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_INPUT_SUFFIX_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                placeholder: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PLACEHOLDER',
                                  },
                                  'x-entity-localized': true,
                                },
                                unit: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'UNIT',
                                  },
                                  'x-entity-presentation': {
                                    hint: {
                                      key: 'ACTIVITY_PERSON_WEIGHT_INPUT_UNIT_HINT',
                                    },
                                  },
                                  'x-entity-localized': true,
                                },
                                prepopulate: {
                                  type: 'boolean',
                                  default: true,
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_PRE_POPULATE',
                                  },
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    minimumValue: {
                                      type: 'number',
                                      default: 0,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_MULTI_SELECT_MIN_VALUE',
                                      },
                                    },
                                    maximumValue: {
                                      type: 'number',
                                      default: 0,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_MULTI_SELECT_MAX_VALUE',
                                      },
                                    },
                                    required: {
                                      type: 'boolean',
                                      default: false,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_REQUIRED',
                                      },
                                      'x-entity-presentation': {
                                        hint: {
                                          key: 'ACTIVITY_INPUT_VALIDATION_REQUIRED_HINT',
                                        },
                                      },
                                    },
                                  },
                                  required: [
                                    'minimumValue',
                                    'maximumValue',
                                    'required',
                                  ],
                                },
                                componentStorageSource: {
                                  title: 'QuestionnaireStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_QUESTIONNAIRE_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_QUESTIONNAIRE',
                                      },
                                      enum: ['fhir-questionnaire'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        questionnaireReference: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_QUESTIONNAIRE_REFERENCE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        linkId: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'ACTIVITY_LINK_ID',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                        {
                          type: 'object',
                          'x-entity-label': {
                            key: 'ACTIVITY_BIOMETRIC_COMPONENT',
                          },
                          properties: {
                            id: {
                              type: 'string',
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentType: {
                              type: 'string',
                              'x-entity-label': {
                                key: 'ACTIVITY_BIOMETRIC',
                              },
                              enum: ['biometric'],
                              'x-entity-presentation': {
                                hidden: true,
                              },
                            },
                            componentAttributes: {
                              type: 'object',
                              'x-entity-label': {
                                key: 'ACTIVITY_BIOMETRIC_COMPONENT_ATTRIBUTES',
                              },
                              required: ['value', 'componentStorageSource'],
                              properties: {
                                iconUrl: {
                                  type: 'string',
                                  format: 'uri-reference',
                                  'x-entity-asset': {
                                    type: 'image',
                                  },
                                  'x-entity-label': {
                                    key: 'ACTIVITY_ICON_URL',
                                  },
                                },
                                name: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'NAME',
                                  },
                                  'x-entity-localized': true,
                                },
                                value: {
                                  type: 'number',
                                  default: 0,
                                  'x-entity-label': {
                                    key: 'VALUE',
                                  },
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                unit: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'UNIT',
                                  },
                                  'x-entity-localized': true,
                                },
                                collectedOn: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BIOMETRIC_COLLECTED_ON',
                                  },
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                status: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'STATUS',
                                  },
                                  'x-entity-presentation': {
                                    hidden: true,
                                    enumLabels: {
                                      enumControlPlaceholder:
                                        'ACTIVITY_BIOMETRIC_STATUS_PLACEHOLDER',
                                      'in-range':
                                        'ACTIVITY_BIOMETRIC_STATUS_IN_RANGE',
                                      'out-of-range':
                                        'ACTIVITY_BIOMETRIC_STATUS_OUT_OF_RANGE',
                                      inconclusive:
                                        'ACTIVITY_BIOMETRIC_STATUS_INCONCLUSIVE',
                                      'no-data-available':
                                        'ACTIVITY_BIOMETRIC_STATUS_NO_DATA_AVAILABLE',
                                    },
                                  },
                                  enum: [
                                    'in-range',
                                    'out-of-range',
                                    'inconclusive',
                                    'no-data-available',
                                  ],
                                },
                                statusText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_BIOMETRIC_STATUS_TEXT',
                                  },
                                  'x-entity-presentation': {
                                    hidden: true,
                                  },
                                },
                                ctaUrl: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_CTA_URL',
                                  },
                                  'x-entity-localized': true,
                                },
                                ctaText: {
                                  type: 'string',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_CTA_TEXT',
                                  },
                                  'x-entity-localized': true,
                                },
                                validation: {
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_INPUT_VALIDATION',
                                  },
                                  properties: {
                                    minimumValue: {
                                      type: 'number',
                                      default: 0,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_MIN_VALUE',
                                      },
                                    },
                                    maximumValue: {
                                      type: 'number',
                                      default: 0,
                                      'x-entity-label': {
                                        key: 'ACTIVITY_INPUT_VALIDATION_MAX_VALUE',
                                      },
                                    },
                                  },
                                  required: ['minimumValue', 'maximumValue'],
                                },
                                componentStorageSource: {
                                  title: 'ObservationStorageSourceAttributes',
                                  type: 'object',
                                  'x-entity-label': {
                                    key: 'ACTIVITY_OBSERVATION_STORAGE_SOURCE_ATTRIBUTES',
                                  },
                                  properties: {
                                    storageSourceType: {
                                      type: 'string',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_FHIR_OBSERVATION',
                                      },
                                      enum: ['fhir-observation'],
                                    },
                                    storageSourceAttributes: {
                                      type: 'object',
                                      'x-entity-label': {
                                        key: 'ACTIVITY_STORAGE_SOURCE_ATTRIBUTES',
                                      },
                                      properties: {
                                        code: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'CODE',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        metric: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'METRIC',
                                          },
                                          'x-entity-localized': true,
                                        },
                                        event: {
                                          type: 'string',
                                          'x-entity-label': {
                                            key: 'EVENT',
                                          },
                                          description:
                                            'valueConcept field represents the FHIR observation value, if the value is a CodeableConcept',
                                          'x-entity-localized': true,
                                        },
                                      },
                                    },
                                  },
                                  required: ['storageSourceType'],
                                },
                              },
                            },
                          },
                          required: ['componentType', 'componentAttributes'],
                        },
                      ],
                    },
                  },
                  branchingLogic: {
                    title: 'BranchingLogicAttributes',
                    'x-entity-label': {
                      key: 'ACTIVITY_BRANCHING_LOGIC',
                    },
                    'x-entity-presentation': {
                      hint: {
                        key: 'ACTIVITY_BRANCHING_LOGIC_HINT',
                      },
                    },
                    type: 'object',
                    properties: {
                      rule: {
                        type: 'string',
                        format: 'json-logic',
                        'x-entity-label': {
                          key: 'RULE',
                        },
                        'x-entity-presentation': {
                          hint: {
                            key: 'ACTIVITY_RULE_HINT',
                          },
                        },
                      },
                      data: {
                        type: 'object',
                        'x-entity-presentation': {
                          hidden: true,
                        },
                        properties: {},
                      },
                      ruleString: {
                        type: 'string',
                        'x-entity-presentation': {
                          hidden: true,
                        },
                      },
                      dataString: {
                        type: 'string',
                        'x-entity-presentation': {
                          hidden: true,
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
        assignmentData: {
          type: 'object',
          'x-entity-label': {
            key: 'ACTIVITY_ASSIGNMENT_DATA',
          },
          properties: {
            required: {
              type: 'boolean',
              'x-entity-label': {
                key: 'ACTIVITY_REQUIRED',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_REQUIRED_HINT',
                },
              },
              default: false,
            },
            removable: {
              type: 'boolean',
              'x-entity-label': {
                key: 'ACTIVITY_REMOVABLE',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_REMOVABLE_HINT',
                },
              },
              default: false,
            },
            activityDate: {
              type: 'object',
              'x-entity-label': {
                key: 'ACTIVITY_DURATION',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_DURATION_HINT',
                },
              },
              properties: {
                startDate: {
                  type: 'string',
                  'x-entity-label': {
                    key: 'ACTIVITY_START_DATE',
                  },
                  format: 'date',
                  description: 'Start date of an activity.',
                },
                endDate: {
                  type: 'string',
                  'x-entity-label': {
                    key: 'ACTIVITY_END_DATE',
                  },
                  format: 'date',
                  description: 'End date of an activity.',
                },
              },
            },
            eligibilityCriteria: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_ELIGIBILITY_CRITERIA',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_ELIGIBILITY_CRITERIA_HINT',
                },
              },
              'x-entity-control': {
                type: 'json-logic',
                options: {
                  jsonLogic: {
                    rulesTemplateType: 'eligibilityCriteria',
                  },
                },
              },
              format: 'json-logic',
              description: 'Rules for eligibility for this activity.',
            },
          },
          required: ['required', 'removable'],
        },
        completionData: {
          type: 'object',
          'x-entity-label': {
            key: 'ACTIVITY_COMPLETION_DATA',
          },
          'x-entity-presentation': {
            hint: {
              key: 'ACTIVITY_COMPLETION_HINT',
            },
            banner: {
              title: {
                key: 'ACTIVITY_COMPLETION_BANNER_TITLE',
              },
              description: {
                key: 'ACTIVITY_COMPLETION_BANNER_DESCRIPTION',
              },
              status: 'warning',
            },
          },
          properties: {
            completionCriteria: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_COMPLETION_CRITERIA',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_COMPLETION_CRITERIA_HINT',
                },
              },
              'x-entity-control': {
                type: 'json-logic',
                options: {
                  jsonLogic: {
                    rulesTemplateType: 'completionCriteria',
                  },
                },
              },
              format: 'json-logic',
              description: 'Rules for completing an activity.',
            },
            loopbackStartDate: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_LOOK_BACK_START_DATE',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_LOOK_BACK_START_DATE_HINT',
                },
              },
              format: 'date',
              description:
                'Eligibility start date for completing a no-touch activity.',
            },
            loopbackEndDate: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_LOOK_BACK_END_DATE',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_LOOK_BACK_END_DATE_HINT',
                },
              },
              format: 'date',
              description:
                'Eligibility end date for completing a no-touch activity.',
            },
            visibilityEndDate: {
              type: 'boolean',
              default: false,
              'x-entity-label': {
                key: 'ACTIVITY_VISIBILITY_END_DATE',
              },
              description: 'Expire by Visible End Date.',
            },
            eventId: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_EVENT_ID',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_EVENT_ID_HINT',
                },
              },
              description:
                'Identifiers for the events that mark this Activity as complete. All must be triggered.',
              'x-entity-localized': true,
            },
            campaignTypeTrigger: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_CAMPAIGN_TYPE_TRIGGER',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_CAMPAIGN_TYPE_TRIGGER_HINT',
                },
                enumLabels: {
                  enumControlPlaceholder:
                    'ACTIVITY_CAMPAIGN_TYPE_TRIGGER_PLACEHOLDER',
                  'opt-in': 'ACTIVITY_OPT_IN',
                  challenge: 'CHALLENGE',
                },
              },
              enum: ['', 'opt-in', 'challenge'],
              description:
                'This field is used to specify the type of campaign that triggers the no-touch activity.',
            },
            triggerCampaignId: {
              type: 'string',
              'x-entity-reference': {
                domainEntityType: 'config-campaign',
              },
              'x-entity-label': {
                key: 'ACTIVITY_TRIGGER_CAMPAIGN_ID',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_TRIGGER_CAMPAIGN_ID_HINT',
                },
              },
              description:
                'If there is a specific Campaign that triggers the No-touch completion criteria, link to it here.',
            },
          },
          required: ['visibilityEndDate'],
        },
        rewardData: {
          type: 'object',
          'x-entity-label': {
            key: 'ACTIVITY_REWARDS_DATA',
          },
          properties: {
            points: {
              type: 'integer',
              'x-entity-label': {
                key: 'ACTIVITY_POINTS',
              },
              minimum: 0,
              default: 0,
              description: 'Awarded Currency on Completion.',
            },
            rewardCriteria: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_REWARD_CRITERIA',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'ACTIVITY_REWARD_CRITERIA_HINT',
                },
              },
              'x-entity-control': {
                type: 'json-logic',
                options: {
                  jsonLogic: {
                    rulesTemplateType: 'rewardCriteria',
                  },
                },
              },
              format: 'json-logic',
            },
            unit: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_UNIT',
              },
              'x-entity-localized': true,
            },
            badge: {
              type: 'string',
              'x-entity-label': {
                key: 'ACTIVITY_BADGE',
              },
              format: 'uri-reference',
              'x-entity-asset': {
                type: 'image',
              },
            },
          },
          required: ['points'],
        },
      },
      required: [
        'entityMetadata',
        'metaData',
        'cardData',
        'contentData',
        'rewardData',
      ],
    },
  },
  required: ['id', 'type'],
};

export const mockCampaignCreateSchema = {
  title: 'ConfigCampaignRequest',
  'x-stoplight': {
    id: 'tn0npq1b63rzj',
  },
  type: 'object',
  description: "Model for creating a campaign and it's related information.",
  'x-tags': [' Config-Campaign'],
  properties: {
    type: {
      type: 'string',
      'x-stoplight': {
        id: 'd5e67dw6qhk8e',
      },
      enum: ['config-campaign'],
    },
    attributes: {
      title: 'ConfigCampaignAttributes',
      'x-stoplight': {
        id: '5yhia1fpx9aqa',
      },
      'x-entity-label': {
        key: 'CAMPAIGN_ENTITY_NAME',
      },
      type: 'object',
      description: 'Model with config campaign attribute information.',
      'x-tags': [' Config-Campaign'],
      properties: {
        entityMetadata: {
          type: 'object',
          'x-entity-presentation': {
            hidden: true,
          },
          'x-stoplight': {
            id: 'kugiiatjwkgcd',
          },
          required: ['status'],
          description:
            'Entity Related Meta Data. Mostly fields that cannot be updated from the Front End.',
          properties: {
            status: {
              type: 'string',
              'x-stoplight': {
                id: 'de10q2crvdcrw',
              },
              enum: ['draft', 'published'],
              description: 'status of campaign in config tools.\n',
            },
            lastPublishedDate: {
              type: 'string',
              'x-stoplight': {
                id: '68kkljrnotgl8',
              },
              format: 'date-time',
              description: 'Date and time campaign was last published.',
            },
            lastUpdatedDate: {
              type: 'string',
              'x-stoplight': {
                id: 'pic5sm0bt23ci',
              },
              format: 'date-time',
              description: 'date and time campaign was last update.',
            },
          },
        },
        metaData: {
          'x-entity-label': {
            key: 'CAMPAIGN_METADATA_LABEL',
          },
          type: 'object',
          'x-stoplight': {
            id: 'aanxhk7qkfykj',
          },
          properties: {
            internalName: {
              type: 'string',
              'x-stoplight': {
                id: 'eo0iog6894tvy',
              },
              description: 'Searchable within content auth, internal name.',
              'x-entity-label': {
                key: 'CAMPAIGN_METADATA_INTERNAL_NAME_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_METADATA_INTERNAL_NAME_HINT',
                },
              },
            },
            type: {
              type: 'string',
              'x-stoplight': {
                id: 'ru2gxk31ib8na',
              },
              enum: ['challenge', 'auto-assigned', 'reward', 'opt-in'],
              description: 'Defines what type of campaign this is.',
              'x-entity-label': {
                key: 'CAMPAIGN_METADATA_TYPE_LABEL',
              },
            },
            externalId: {
              type: 'string',
              'x-stoplight': {
                id: 'vjpz5h9ssnc4o',
              },
              description:
                'Whether the campaign is used as Blank template for Care Advisor to assign activities to a member, default is False. If it is True, this campaign will be displayed at the top of campaigns available to the Care Advisors for assignment purposes.',
              'x-entity-label': {
                key: 'CAMPAIGN_METADATA_EXTERNAL_ID_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_METADATA_EXTERNAL_ID_HINT',
                },
              },
            },
            campaignDate: {
              type: 'object',
              'x-stoplight': {
                id: 'mqxc9zprnnyg2',
              },
              'x-entity-label': {
                key: 'CAMPAIGN_DATE_LABEL',
              },
              properties: {
                startDate: {
                  type: 'string',
                  'x-stoplight': {
                    id: '4w7rh4jcn7574',
                  },
                  format: 'date',
                  description:
                    'If empty, then users are allowed to participate in the campaign anytime this is live.',
                  'x-entity-label': {
                    key: 'CAMPAIGN_DATE_START_DATE_LABEL',
                  },
                },
                endDate: {
                  type: 'string',
                  'x-stoplight': {
                    id: 'hwoygc6be4j4f',
                  },
                  format: 'date',
                  description:
                    'If empty, then users are allowed to participate in the campaign never expires.',
                  'x-entity-label': {
                    key: 'CAMPAIGN_DATE_END_DATE_LABEL',
                  },
                },
                visibilityEndDate: {
                  type: 'string',
                  'x-stoplight': {
                    id: '707w5jhueqrpq',
                  },
                  format: 'date',
                  description:
                    'The date in which the program is no longer visible. Eg. the program can be visible after the end date for a period of time',
                  'x-entity-presentation': {
                    hint: {
                      key: 'CAMPAIGN_DATE_VISIBILITY_END_DATE_HINT',
                    },
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_DATE_VISIBILITY_END_DATE_LABEL',
                  },
                },
              },
            },
          },
          required: ['internalName', 'type'],
        },
        contentData: {
          type: 'object',
          'x-stoplight': {
            id: '9axsd0fr0v0j9',
          },
          'x-entity-label': {
            key: 'CAMPAIGN_CONTENT_LABEL',
          },
          properties: {
            title: {
              type: 'string',
              'x-stoplight': {
                id: 'ocsyd7rfla1u0',
              },
              description: 'This is the title that the user sees.',
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_TITLE_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_CONTENT_TITLE_HINT',
                },
              },
            },
            programHeader: {
              type: 'string',
              'x-stoplight': {
                id: 're24c19trl28c',
              },
              description:
                'A badge that appears above the title of the program.',
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_PROGRAM_HEADER_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_CONTENT_PROGRAM_HEADER_HINT',
                },
              },
            },
            shortDescription: {
              type: 'string',
              'x-stoplight': {
                id: 'ejz148oargvof',
              },
              description:
                'A short summary of campaign. Ensure characters do not exceed 150 character limit.',
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_SHORT_DESCRIPTION_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_CONTENT_SHORT_DESCRIPTION_HINT',
                },
              },
            },
            longDescription: {
              type: 'string',
              'x-stoplight': {
                id: 'i3dghr6s8i683',
              },
              description:
                'Long description of campaign. Ensure characters do not exceed 600 character limit.',
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_LONG_DESCRIPTION_LABEL',
              },
            },
            overview: {
              type: 'string',
              'x-stoplight': {
                id: 'klbm4427k5t1i',
              },
              format: 'rich-text',
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_OVERVIEW_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_CONTENT_OVERVIEW_HINT',
                },
              },
            },
            campaignImageId: {
              type: 'string',
              'x-stoplight': {
                id: '5jm85oemj5q32',
              },
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_CAMPAIGN_IMAGE_ID_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_CONTENT_CAMPAIGN_IMAGE_ID_HINT',
                },
              },
              'x-entity-asset': {
                type: 'image',
              },
            },
            achievement: {
              type: 'object',
              'x-stoplight': {
                id: '06904nbyx6czz',
              },
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_ACHIEVEMENT_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_CONTENT_ACHIEVEMENT_HINT',
                },
              },
              properties: {
                achievementTitle: {
                  type: 'string',
                  'x-stoplight': {
                    id: 'tgqc1l0792kui',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_ACHIEVEMENT_TITLE_LABEL',
                  },
                },
                achievementText: {
                  type: 'string',
                  'x-stoplight': {
                    id: 'ebgyozb92z8g1',
                  },
                  format: 'rich-text',
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_ACHIEVEMENT_TEXT_LABEL',
                  },
                },
                achievementImageId: {
                  type: 'string',
                  'x-stoplight': {
                    id: 'eozdxk18l6xl1',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_ACHIEVEMENT_IMAGE_LABEL',
                  },
                  'x-entity-asset': {
                    type: 'image',
                  },
                },
              },
            },
            globalProgress: {
              type: 'boolean',
              'x-stoplight': {
                id: 'pidombm7vwltn',
              },
              default: false,
              description:
                'Determines if a progress bar is displayed in the Campaign   Listing.',
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_ACHIEVEMENT_GLOBAL_PROGRESS_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_CONTENT_ACHIEVEMENT_GLOBAL_PROGRESS_HINT',
                },
              },
            },
            disclaimerBanner: {
              type: 'object',
              'x-stoplight': {
                id: 'wdjbzmq42e80s',
              },
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_DISCLAIMER_BANNER',
              },
              properties: {
                title: {
                  type: 'string',
                  'x-stoplight': {
                    id: 'glnc2qcsbj449',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_DISCLAIMER_BANNER_TITLE_LABEL',
                  },
                },
                description: {
                  type: 'string',
                  'x-stoplight': {
                    id: '6vdfk47yjpnmh',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_DISCLAIMER_BANNER_DESCRIPTION_LABEL',
                  },
                },
                iconImageId: {
                  type: 'string',
                  'x-stoplight': {
                    id: '9z7z386cmm5kt',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_DISCLAIMER_BANNER_ICON_IMAGE_ID_LABEL',
                  },
                  'x-entity-asset': {
                    type: 'image',
                  },
                },
              },
            },
            programRulesText: {
              type: 'string',
              'x-stoplight': {
                id: 'u7p7wazcxnyzz',
              },
              format: 'rich-text',
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_PROGRAM_RULES_LABEL',
              },
            },
            contentProvider: {
              type: 'object',
              'x-stoplight': {
                id: 'n16sq4qt685kr',
              },
              'x-entity-label': {
                key: 'CAMPAIGN_CONTENT_CONTENT_PROVIDER_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_CONTENT_CONTENT_PROVIDER_HINT',
                },
              },
              properties: {
                name: {
                  type: 'string',
                  'x-stoplight': {
                    id: 'lyxgzt8bscugw',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_CONTENT_PROVIDER_NAME_LABEL',
                  },
                },
                description: {
                  type: 'string',
                  'x-stoplight': {
                    id: '706facub4cbsm',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_CONTENT_PROVIDER_DESCRIPTION_LABEL',
                  },
                },
                logoMarkImageId: {
                  type: 'string',
                  'x-stoplight': {
                    id: 'uq1s7qgct58gf',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_CONTENT_PROVIDER_LOGOTYPE_IMAGE_ID_LABEL',
                  },
                  'x-entity-asset': {
                    type: 'image',
                  },
                },
                logoTypeImageId: {
                  type: 'string',
                  'x-stoplight': {
                    id: '7dnuqknjz74wf',
                  },
                  'x-entity-label': {
                    key: 'CAMPAIGN_CONTENT_CONTENT_PROVIDER_LOGOMARK_IMAGE_ID_LABEL',
                  },
                  'x-entity-asset': {
                    type: 'image',
                  },
                },
              },
            },
          },
          required: ['title', 'overview', 'campaignImageId'],
        },
        enrolmentData: {
          type: 'object',
          'x-stoplight': {
            id: '9ud9o257mgxsv',
          },
          'x-entity-label': {
            key: 'CAMPAIGN_ENROLMENT_LABEL',
          },
          'x-entity-inject-reference': {
            relationship: 'campaigns',
            labelKey: 'CAMPAIGN_ENROLMENT_CHILD_CAMPAIGNS_LABEL',
            arrayAddLabel: 'CAMPAIGN_ENROLMENT_CHILD_CAMPAIGNS_ADD_LABEL',
            hint: {
              key: 'CAMPAIGN_ENROLMENT_CHILD_CAMPAIGNS_HINT',
            },
          },
          properties: {
            eligibilityCriteria: {
              type: 'string',
              'x-stoplight': {
                id: 'n1bh74r6gq8m3',
              },
              description: 'If empty, everyone is eligible.',
              format: 'json-logic',
              'x-entity-label': {
                key: 'CAMPAIGN_ENROLMENT_ELIGIBILITY_CRITERIA_LABEL',
              },
              'x-entity-control': {
                type: 'json-logic',
                options: {
                  jsonLogic: {
                    rulesTemplateType: 'eligibilityCriteria',
                  },
                },
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_ENROLMENT_ELIGIBILITY_CRITERIA_HINT',
                },
              },
            },
            enrolmentMethod: {
              type: 'string',
              'x-stoplight': {
                id: '5ud891nkni9aq',
              },
              enum: ['opt-in', 'auto-enrolled', 'care-team-member'],
              description:
                'Represents the type of individual who makes the request to enroll the user into the campaign. care_team_member: Care Advisor is assigning/editing this campaign. league_system: Members are auto enrolled into those campaigns - suggested campaigns and reward programs.',
              'x-entity-label': {
                key: 'CAMPAIGN_ENROLMENT_ENROLMENT_METHOD_LABEL',
              },
            },
            enrolment: {
              type: 'object',
              'x-stoplight': {
                id: 'dyytwkmzaf8l4',
              },
              'x-entity-label': {
                key: 'CAMPAIGN_ENROLMENT_ENROLMENT_DURATION_LABEL',
              },
              properties: {
                startDate: {
                  type: 'string',
                  'x-stoplight': {
                    id: '8bdge921g6013',
                  },
                  format: 'date',
                  description:
                    'Date user can start enrolment. If empty, then users are allowed to enroll anytime this is live.',
                  'x-entity-label': {
                    key: 'CAMPAIGN_ENROLMENT_ENROLMENT_DURATION_START_DATE_LABEL',
                  },
                },
                endDate: {
                  type: 'string',
                  'x-stoplight': {
                    id: 'gdjvym7ealsel',
                  },
                  format: 'date',
                  description: 'End date for user enrolment.',
                  'x-entity-label': {
                    key: 'CAMPAIGN_ENROLMENT_ENROLMENT_DURATION_END_DATE_LABEL',
                  },
                },
              },
            },
            frontLineCampaign: {
              type: 'boolean',
              'x-stoplight': {
                id: 'ndwmnzoyjopvj',
              },
              default: false,
              description:
                'Determines if this Campaign will take priority over other recommended campaigns.',
              'x-entity-label': {
                key: 'CAMPAIGN_ENROLMENT_FRONT_LINE_CAMPAIGN_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_ENROLMENT_FRONT_LINE_CAMPAIGN_HINT',
                },
              },
            },
            rootCampaign: {
              type: 'boolean',
              'x-stoplight': {
                id: 'u1kuunji033gm',
              },
              default: false,
              'x-entity-label': {
                key: 'CAMPAIGN_ENROLMENT_ROOT_CAMPAIGN_LABEL',
              },
            },
          },
          required: ['enrolmentMethod'],
        },
        completionData: {
          type: 'object',
          'x-stoplight': {
            id: 'sx0p7yygz20l0',
          },
          'x-entity-label': {
            key: 'CAMPAIGN_COMPLETION_LABEL',
          },
          properties: {
            completionCriteria: {
              type: 'string',
              'x-stoplight': {
                id: 'eyio3an77jkt9',
              },
              description:
                'If empty, users need to complete all items within the campaign.',
              format: 'json-logic',
              'x-entity-label': {
                key: 'CAMPAIGN_COMPLETION_COMPLETION_CRITERIA_LABEL',
              },
              'x-entity-control': {
                type: 'json-logic',
                options: {
                  jsonLogic: {
                    rulesTemplateType: 'completionCriteria',
                  },
                },
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_COMPLETION_COMPLETION_CRITERIA_HINT',
                },
              },
            },
            maxRedemptionPoint: {
              type: 'integer',
              'x-stoplight': {
                id: 'cfiflyr689ke7',
              },
              minimum: 0,
              description:
                'Determines the maximum number of points that will be rewarded for all child Campaigns and Activities.',
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_COMPLETION_MAX_REDEMPTION_POINTS_HINT',
                },
              },
              'x-entity-label': {
                key: 'CAMPAIGN_COMPLETION_MAX_REDEMPTION_POINTS_LABEL',
              },
            },
            rewardProgramGoal: {
              type: 'integer',
              'x-stoplight': {
                id: 'md1v5tss8kndb',
              },
              minimum: 0,
              description:
                'If this campaign is eligible for rewards programs, specify the number of activities in the campaign that a user must complete in order to earn reward points.',
              'x-entity-label': {
                key: 'CAMPAIGN_COMPLETION_REWARD_PROGRAM_GOAL_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_COMPLETION_REWARD_PROGRAM_GOAL_HINT',
                },
              },
            },
            programCompletionTitle: {
              type: 'string',
              'x-stoplight': {
                id: '3fu5b861ew3zn',
              },
              description:
                'Title to display on card when the Campaign is completed.',
              'x-entity-label': {
                key: 'CAMPAIGN_COMPLETION_PROGRAM_COMPLETION_TITLE_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_COMPLETION_PROGRAM_COMPLETION_TITLE_HINT',
                },
              },
            },
            completionMessage: {
              type: 'string',
              'x-stoplight': {
                id: '5qgah6rguv0kc',
              },
              format: 'rich-text',
              'x-entity-label': {
                key: 'CAMPAIGN_COMPLETION_COMPLETION_MESSAGE_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_COMPLETION_COMPLETION_MESSAGE_HINT',
                },
              },
            },
            programEndedTitle: {
              type: 'string',
              'x-stoplight': {
                id: 'tepv4q8y4k1pj',
              },
              description:
                'Title to display on card when the Campaign has ended.',
              'x-entity-label': {
                key: 'CAMPAIGN_COMPLETION_PROGRAM_ENDED_TITLE_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_COMPLETION_PROGRAM_ENDED_TITLE_HINT',
                },
              },
            },
            endedMessage: {
              type: 'string',
              'x-stoplight': {
                id: 'xdfzkg0jrmm04',
              },
              format: 'rich-text',
              'x-entity-label': {
                key: 'CAMPAIGN_COMPLETION_ENDED_MESSAGE_LABEL',
              },
              'x-entity-presentation': {
                hint: {
                  key: 'CAMPAIGN_COMPLETION_ENDED_MESSAGE_HINT',
                },
              },
            },
          },
          required: ['programCompletionTitle', 'programEndedTitle'],
        },
        flowTemplate: {
          type: 'object',
          'x-entity-label': {
            key: 'CAMPAIGN_FLOW',
          },
          'x-entity-control': {
            type: 'flow' as const,
            options: {
              flow: {
                flowNodes: 'campaignNodes',
              },
            },
          },
          properties: {
            campaignNodes: {
              type: 'array',
              'x-stoplight': {
                id: 'hlspmia1czpbq',
              },
              'x-entity-label': {
                key: 'CAMPAIGN_CAMPAIGN_NODES_LABEL',
              },
              items: {
                title: 'ConfigCampaignNode',
                'x-stoplight': {
                  id: 'xxuiv58xd6wve',
                },
                type: 'object',
                'x-tags': ['Config-Campaign'],
                'x-entity-label': {
                  key: 'CAMPAIGN_CAMPAIGN_NODE_LABEL',
                  propertyRef: '$.nodeName',
                },
                description:
                  'Model with config campaign activity node information.',
                properties: {
                  nodeName: {
                    type: 'string',
                    'x-stoplight': {
                      id: '3stq0fyeib3s8',
                    },
                    'x-entity-label': {
                      key: 'CAMPAIGN_CAMPAIGN_NODES_NODE_NAME_LABEL',
                    },
                  },
                  offset: {
                    type: 'integer',
                    'x-stoplight': {
                      id: 'r52qfg3mwsr04',
                    },
                    minimum: 0,
                    'x-entity-label': {
                      key: 'CAMPAIGN_CAMPAIGN_NODES_OFFSET_LABEL',
                    },
                  },
                  activities: {
                    type: 'array',
                    'x-stoplight': {
                      id: 'wr9k30qaimdw2',
                    },
                    'x-entity-label': {
                      key: 'CAMPAIGN_CAMPAIGN_NODES_ACTIVITIES_LABEL',
                    },
                    items: {
                      title: 'ConfigCampaignNodeActivity',
                      'x-stoplight': {
                        id: 'aoa347jl5ghte',
                      },
                      type: 'object',
                      'x-entity-label': {
                        key: 'ACTIVITY',
                      },
                      description:
                        'Model with config campaign activity information.',
                      properties: {
                        id: {
                          type: 'string',
                          'x-entity-label': {
                            key: 'CAMPAIGN_CAMPAIGN_NODES_ACTIVITIES_ACTIVITY_ID_LABEL',
                          },
                          'x-entity-reference': {
                            relationship: 'activity',
                          },
                        },
                        canExpire: {
                          type: 'boolean',
                          'x-stoplight': {
                            id: '4re5eiqrzstlz',
                          },
                          default: false,
                          'x-entity-label': {
                            key: 'CAMPAIGN_CAMPAIGN_NODES_ACTIVITIES_CAN_EXPIRE_LABEL',
                          },
                        },
                        expireIn: {
                          type: 'integer',
                          'x-stoplight': {
                            id: '4dzghkmvi1p63',
                          },
                          minimum: 1,
                          description:
                            'Any integer from 1 to 365 is allowed. 1 means in 1 Day.',
                          'x-entity-label': {
                            key: 'CAMPAIGN_CAMPAIGN_NODES_ACTIVITIES_EXPIRE_IN_LABEL',
                          },
                          'x-entity-presentation': {
                            hint: {
                              key: 'CAMPAIGN_CAMPAIGN_NODES_ACTIVITIES_EXPIRE_IN_HINT',
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      required: ['entityMetadata'],
    },
    relationships: {
      title: 'ConfigCampaignRelationships',
      'x-stoplight': {
        id: 'ftcdn2je5v0ms',
      },
      type: 'object',
      'x-tags': ['Config-Campaign'],
      properties: {
        activity: {
          type: 'object',
          'x-stoplight': {
            id: '8l96n9w8kd0j6',
          },
          properties: {
            data: {
              type: 'array',
              'x-stoplight': {
                id: 'ec9cmqx1lk967',
              },
              items: {
                'x-stoplight': {
                  id: 'vqazizmby8ni4',
                },
                type: 'object',
                properties: {
                  id: {
                    type: 'string',
                    'x-stoplight': {
                      id: '4jjmk9fy9a32t',
                    },
                  },
                  type: {
                    type: 'string',
                    'x-stoplight': {
                      id: 'f3exkvuh2m2gh',
                    },
                    enum: ['config-activity'],
                  },
                },
              },
            },
          },
        },
        campaigns: {
          type: 'object',
          'x-stoplight': {
            id: '8l96n9w8kd0j6',
          },
          properties: {
            data: {
              type: 'array',
              'x-stoplight': {
                id: 'ec9cmqx1lk967',
              },
              items: {
                'x-stoplight': {
                  id: 'vqazizmby8ni4',
                },
                type: 'object',
                properties: {
                  id: {
                    type: 'string',
                    'x-stoplight': {
                      id: '4jjmk9fy9a32t',
                    },
                  },
                  type: {
                    type: 'string',
                    'x-stoplight': {
                      id: 'f3exkvuh2m2gh',
                    },
                    enum: ['config-campaign'],
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  required: ['type'],
};

export const mockObjectCardSchema = {
  type: 'array',
  'x-entity-label': {
    key: 'THING',
  },
  'x-entity-control': {
    type: 'objectCard',
  },
  items: {
    type: 'object',
    'x-entity-label': {
      key: 'SIMPLE_SCHEMA_ARRAY_ITEM_LABEL',
    },
    properties: {
      internalName: {
        type: 'string',
        'x-entity-label': {
          key: 'SIMPLE_SCHEMA_THING_LABEL',
        },
      },
      action: {
        type: 'object',
        'x-entity-label': {
          key: 'SIMPLE_SCHEMA_THING_LABEL',
        },
        properties: {
          url: {
            type: 'string',
            'x-entity-label': {
              key: 'SIMPLE_SCHEMA_THING_LABEL',
            },
          },
          title: {
            type: 'string',
            'x-entity-label': {
              key: 'SIMPLE_SCHEMA_THING_LABEL',
            },
          },
        },
      },
    },
  },
};
